import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Key,
  Camera,
  Loader,
  X,
  HardDrive,
  Users,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { db, storage } from "../../firebase";
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import Toast from "../Toast/Toast";
import { sendPasswordResetEmail } from "firebase/auth";
import { getUserPlan } from "../../utils/planUtils";
import { useStorageUsage } from "../../utils/StorageTracker";

// Helper functions for image handling
const validateImage = (file) => {
  // Check file type
  if (!file.type.match("image/jpeg") && !file.type.match("image/png")) {
    return "Only JPEG and PNG images are supported";
  }

  // Check file size (limit to 2MB)
  if (file.size > 2 * 1024 * 1024) {
    return "Image size should be less than 2MB";
  }

  return null;
};

const processImage = async (file) => {
  return new Promise((resolve) => {
    // For now, just return the file directly
    // In a production app, you might want to resize/compress it
    resolve(file);
  });
};

const UserProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [displayIdError, setDisplayIdError] = useState("");
  const [userPlan, setUserPlan] = useState(null);
  const [accounts, setAccounts] = useState([]);

  // Profile Data
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    userId: "",
    displayId: "",
    avatarUrl: "",
  });

  const { user } = useAuth();
  const {
    usage,
    isLoading,
    isOverLimit,
    percentUsed,
    formattedUsage,
    formattedMaxStorage,
  } = useStorageUsage(user?.uid);

  // Fetch user plan and storage info
  useEffect(() => {
    const fetchPlanAndStorage = async () => {
      if (!user) return;
      try {
        const plan = await getUserPlan(user.uid);
        setUserPlan(plan);
      } catch (error) {
        console.error("Error fetching plan info:", error);
      }
    };

    fetchPlanAndStorage();
  }, [user]);

  // Fetch user accounts (for Pro users)
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user) return;
      try {
        const accountsRef = collection(db, "accountProfiles");
        const q = query(accountsRef, where("userId", "==", user.uid));
        const querySnapshot = await getDocs(q);

        const accountsList = [];
        querySnapshot.forEach((doc) => {
          accountsList.push({ id: doc.id, ...doc.data() });
        });

        setAccounts(accountsList);
      } catch (error) {
        console.error("Error fetching accounts:", error);
      }
    };

    fetchAccounts();
  }, [user]);

  // Fetch user profile data from Firestore
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setProfileData({
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: user.email || "",
            userId: user.uid,
            displayId: data.displayId || "",
            avatarUrl: data.avatarUrl || "",
          });
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
        setToast({
          type: "error",
          message: "Error Loading Profile",
          subText: "Please try again later",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user]);

  const validateDisplayId = (displayId) => {
    const regex = /^[a-zA-Z0-9_]{3,20}$/;
    return regex.test(displayId);
  };

  const checkDisplayIdAvailability = async (displayId) => {
    if (!displayId || !validateDisplayId(displayId)) return false;
    try {
      const usersRef = collection(db, "users");
      const q = query(
        usersRef,
        where("displayId", "==", displayId.toLowerCase())
      );
      const querySnapshot = await getDocs(q);
      return (
        querySnapshot.empty ||
        (querySnapshot.size === 1 && querySnapshot.docs[0].id === user.uid)
      );
    } catch (error) {
      console.error("Error checking display ID:", error);
      return false;
    }
  };

  // Handle profile image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const error = validateImage(file);
    if (error) {
      setToast({ type: "error", message: error });
      return;
    }

    setIsUploading(true);

    try {
      const processedImage = await processImage(file);
      const storageRef = ref(storage, `users/${user.uid}/profileImage.jpg`);

      await uploadBytes(storageRef, processedImage);
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const downloadURL = await getDownloadURL(storageRef);

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        avatarUrl: downloadURL,
        updatedAt: new Date(),
      });

      setProfileData((prev) => ({
        ...prev,
        avatarUrl: downloadURL,
      }));

      setToast({ type: "success", message: "Profile picture updated" });
    } catch (error) {
      console.error("Upload error:", error);
      setToast({ type: "error", message: "Failed to update profile picture" });
      setProfileData((prev) => ({ ...prev, avatarUrl: "" }));
    } finally {
      setIsUploading(false);
    }
  };

  // Handle profile image removal
  const handleRemoveImage = async () => {
    if (!profileData.avatarUrl) return;

    setIsUploading(true);

    try {
      const storageRef = ref(storage, `users/${user.uid}/profileImage.jpg`);
      await deleteObject(storageRef);

      await new Promise((resolve) => setTimeout(resolve, 2000));

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        avatarUrl: "",
        updatedAt: new Date(),
      });

      setProfileData((prev) => ({
        ...prev,
        avatarUrl: "",
      }));

      setToast({ type: "success", message: "Profile picture removed" });
    } catch (error) {
      console.error("Remove error:", error);
      setToast({ type: "error", message: "Failed to remove profile picture" });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle input changes
  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === "displayId") {
      const formattedValue = value.toLowerCase().replace(/\s/g, "");
      setProfileData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));

      if (formattedValue) {
        if (!validateDisplayId(formattedValue)) {
          setDisplayIdError(
            "Display ID must be 3-20 characters, using only letters, numbers, and underscores"
          );
          return;
        }
        const isAvailable = await checkDisplayIdAvailability(formattedValue);
        if (!isAvailable) {
          setDisplayIdError("This display ID is already taken");
        } else {
          setDisplayIdError("");
        }
      } else {
        setDisplayIdError("");
      }
    } else {
      setProfileData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Validate form fields
  const validateForm = () => {
    if (!profileData.firstName || !profileData.lastName) return false;
    if (!profileData.email.includes("@")) return false;
    if (profileData.displayId && !validateDisplayId(profileData.displayId))
      return false;
    if (displayIdError) return false;
    return true;
  };

  // Handle form submission (update profile)
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!user || !validateForm()) return;

    setSaving(true);
    try {
      if (profileData.displayId) {
        const isAvailable = await checkDisplayIdAvailability(
          profileData.displayId
        );
        if (!isAvailable) {
          setToast({
            type: "error",
            message: "Display ID Unavailable",
            subText: "This display ID was just taken. Please choose another.",
          });
          setSaving(false);
          return;
        }
      }

      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        displayId: profileData.displayId.toLowerCase(),
        updatedAt: new Date(),
      });

      setToast({
        type: "success",
        message: "Profile Updated",
        subText: "Your changes have been saved",
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      setToast({
        type: "error",
        message: "Update Failed",
        subText: error.message,
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!profileData.email) return;

    try {
      await sendPasswordResetEmail(user.auth, profileData.email);
      setToast({
        type: "success",
        message: "Password Reset Email Sent",
        subText: "Please check your email to reset your password",
      });
    } catch (error) {
      setToast({
        type: "error",
        message: "Error",
        subText: "Could not send password reset email",
      });
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader className="animate-spin" />
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          subText={toast.subText}
          onClose={() => setToast(null)}
        />
      )}

      <div className="profile-header">
        <div className="avatar-container">
          <div className="avatar">
            {isUploading ? (
              <div className="avatar-loading">
                <Loader className="animate-spin" size={40} />
              </div>
            ) : profileData.avatarUrl ? (
              <img
                src={profileData.avatarUrl}
                alt="Profile"
                className="avatar-image"
              />
            ) : (
              <div className="avatar-placeholder">
                {profileData.firstName ? (
                  profileData.firstName[0].toUpperCase()
                ) : (
                  <User size={40} />
                )}
              </div>
            )}
            <div className="avatar-controls">
              <label
                className="avatar-upload-button"
                title="Upload new picture"
              >
                <Camera size={20} />
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={isUploading}
                />
              </label>
              {profileData.avatarUrl && (
                <button
                  className="avatar-remove-button"
                  onClick={handleRemoveImage}
                  title="Remove picture"
                  disabled={isUploading}
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleUpdateProfile} className="profile-form">
        {/* Basic Information Section */}
        <div className="form-section">
          <h3>Basic Information</h3>
          <div className="form-group">
            <label htmlFor="userId">User ID (System)</label>
            <div className="input-readonly">
              <User size={20} />
              <input
                type="text"
                id="userId"
                value={profileData.userId}
                readOnly
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="displayId">Display ID</label>
            <div className={`input-with-icon ${displayIdError ? "error" : ""}`}>
              <User size={20} />
              <input
                type="text"
                id="displayId"
                name="displayId"
                value={profileData.displayId}
                onChange={handleChange}
                placeholder="Create your custom display ID"
              />
            </div>
            {displayIdError && (
              <div className="error-text">{displayIdError}</div>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={profileData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={profileData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                required
              />
            </div>
          </div>
        </div>

        {/* Account Settings Section */}
        <div className="form-section">
          <h3>Account Settings</h3>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-with-icon">
              <Mail size={20} />
              <input
                type="email"
                id="email"
                name="email"
                value={profileData.email}
                readOnly
                className="input-readonly"
              />
            </div>
          </div>
          <div className="form-group">
            <button
              type="button"
              onClick={handleResetPassword}
              className="btn-secondary"
            >
              <Key size={20} />
              Reset Password
            </button>
          </div>
        </div>

        {/* Plan Details Section */}
        {userPlan && (
          <div className="form-section">
            <h3>Plan Details</h3>
            <div className="plan-details">
              <div className="plan-type">
                <h4>{userPlan.name} Plan</h4>
                {userPlan.planType === "standard" && (
                  <button className="upgrade-button">Upgrade to Pro</button>
                )}
              </div>

              {/* Storage Usage */}
              <div className="usage-metrics">
                <div className="storage-usage">
                  <h5>
                    <HardDrive size={16} /> Storage Usage
                  </h5>
                  <div className="progress-bar">
                    <div
                      className="progress"
                      style={{
                        width: `${Math.min(percentUsed, 100)}%`,
                        backgroundColor:
                          percentUsed > 90
                            ? "#ef4444"
                            : percentUsed > 70
                            ? "#f59e0b"
                            : "var(--accent-color)",
                      }}
                    ></div>
                  </div>
                  <p>
                    {isLoading ? (
                      "Loading..."
                    ) : isOverLimit ? (
                      <span
                        style={{
                          color: "#ef4444",
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <AlertTriangle size={16} /> Storage limit exceeded!
                      </span>
                    ) : (
                      `${formattedUsage} of ${formattedMaxStorage} used (${Math.round(
                        percentUsed
                      )}%)`
                    )}
                  </p>
                </div>

                {/* Account Usage Meter */}
                <div className="account-usage">
                  <h5>
                    <Users size={16} /> Account Usage
                  </h5>
                  <div className="progress-bar">
                    <div
                      className="progress"
                      style={{
                        width: `${
                          (accounts.length / (userPlan?.maxAccounts || 1)) * 100
                        }%`,
                        backgroundColor:
                          accounts.length >= userPlan?.maxAccounts
                            ? "#ef4444"
                            : "var(--accent-color)",
                      }}
                    ></div>
                  </div>
                  <p>
                    {accounts.length} account{accounts.length !== 1 ? "s" : ""}{" "}
                    of {userPlan?.maxAccounts || 1} available
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Save Changes Button */}
        <div className="form-actions">
          <button
            type="submit"
            className="btn-primary"
            disabled={saving || !validateForm()}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserProfile;
